import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { DEFAULT_RUBRIC_SCALE_MAX, RUBRIC_SCALE_MAXES, RUBRIC_SCALE_MIN, type RubricScaleMax, type RubricView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Chip } from '../../../../components/Chip';
import { Field, FieldRow } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { Segmented } from '../../../../components/Segmented';
import { Switch } from '../../../../components/Switch';
import { errorMessage } from '../../../../lib/api-client';
import { formatDate } from '../../../../lib/format';
import { useCanEdit } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { useRubricVersions, useSaveRubric } from '../../api/evaluation-api';
import { useLeaveGuard } from './LeaveGuard';
import { MoveMenu, moveItem, useDragReorder } from './reorder';
import styles from './Evaluation.module.css';

/**
 * ADMIN-02, one rubric (admin redesign batch 2, Option A): name, scale and what it's for; its lines, each with what good looks
 * like and Must pass, in an order set by dragging or the line's "Move to…" menu; and the versions line.
 *
 * Saving writes the next version, and every line keeps its key, so a kit's questions still find their line. Weights are not
 * shown: they count for nothing since ADR 0077, so a number to tune would only invite tuning that changes nothing. Each line's
 * stored weight is sent back as it was (a new line takes 1), so nothing written before changes meaning.
 */

export const MAX_RUBRIC_LINES = 15;

interface LineDraft {
  /** The editor's own id for React; `key` is the server's, kept across versions. */
  readonly rowId: string;
  readonly key: string | undefined;
  readonly label: string;
  readonly description: string;
  readonly weight: number;
  readonly isCritical: boolean;
}

interface Draft {
  readonly name: string;
  readonly description: string;
  readonly scaleMax: RubricScaleMax;
  readonly lines: readonly LineDraft[];
}

function draftOf(rubric: RubricView | null): Draft {
  return {
    name: rubric?.name ?? '',
    description: rubric?.description ?? '',
    scaleMax: asScale(rubric?.scaleMax),
    lines: rubric
      ? rubric.criteria.map((line) => ({ rowId: line.id, key: line.key, label: line.label, description: line.description ?? '', weight: line.weight, isCritical: line.isCritical }))
      : [newLine()],
  };
}

function newLine(): LineDraft {
  return { rowId: crypto.randomUUID(), key: undefined, label: '', description: '', weight: 1, isCritical: false };
}

/** What still stops a save, in plain words; empty when it can go. */
export function rubricProblems(draft: Draft): readonly string[] {
  const problems: string[] = [];
  if (draft.name.trim().length < 2) problems.push('Name the rubric.');
  if (draft.lines.length === 0) problems.push('Add at least one line.');
  if (draft.lines.some((line) => line.label.trim() === '')) problems.push('Every line needs a name.');
  return problems;
}

export function RubricEditor({ rubric, current }: { readonly rubric: RubricView | null; readonly current: RubricView | null }): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const save = useSaveRubric();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const versions = useRubricVersions(rubric?.id ?? null);
  // The version this draft started from — never the one a refetch brought (review W7).
  const [base] = useState(rubric);
  const [draft, setDraft] = useState<Draft>(() => draftOf(rubric));
  const [isDirty, setIsDirty] = useState(false);
  const { prompt, release } = useLeaveGuard(isDirty);

  const isOlder = base !== null && !base.isCurrent;
  // Someone saved a newer version while this one was open: ours can no longer be saved over it.
  // Our own save replaces this version too, a moment before its answer is back; that is not someone else's.
  const [isSaving, setIsSaving] = useState(false);
  const isOvertaken = base !== null && base.isCurrent && rubric !== null && !rubric.isCurrent && !isSaving;
  const isReadOnly = !canEdit || isOlder || isOvertaken;
  const problems = rubricProblems(draft);
  const lines = draft.lines;

  const change = (patch: Partial<Draft>): void => {
    setDraft((previous) => ({ ...previous, ...patch }));
    setIsDirty(true);
  };
  const setLine = (index: number, patch: Partial<LineDraft>): void => change({ lines: lines.map((line, at) => (at === index ? { ...line, ...patch } : line)) });
  const moveLine = (from: number, to: number): void => change({ lines: moveItem(lines, from, to) });
  const { attach, itemProps, handle } = useDragReorder<HTMLOListElement>(lines.length, moveLine, !isReadOnly);

  function cancel(): void {
    if (!base) {
      release();
      void navigate({ to: '/admin/rubrics' });
      return;
    }
    setDraft(draftOf(base));
    setIsDirty(false);
  }

  async function onSave(): Promise<void> {
    if (problems.length > 0 || isReadOnly) return;
    setIsSaving(true);
    try {
      const saved = await save.mutateAsync({
        id: base?.id ?? null,
        version: base?.version ?? 0,
        body: {
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          scaleMax: draft.scaleMax,
          criteria: lines.map((line) => ({
            ...(line.key ? { key: line.key } : {}),
            label: line.label.trim(),
            description: line.description.trim() || null,
            weight: line.weight,
            isCritical: line.isCritical,
          })),
        },
      });
      setIsDirty(false);
      release();
      show({
        tone: 'success',
        message: `${saved.name} saved as version ${saved.versionNumber}`,
        detail: base ? `Rounds using version ${base.versionNumber} keep it.` : "Interview kits can group their questions under its lines now.",
      });
      void navigate({ to: '/admin/rubrics/$rubricId', params: { rubricId: saved.id } });
    } catch (cause) {
      setIsSaving(false);
      show({ tone: 'danger', message: 'The rubric was not saved', detail: errorMessage(cause) });
    }
  }

  // 06-qol §2: Ctrl/Cmd+Enter saves, as every form does.
  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && isDirty) {
      event.preventDefault();
      void onSave();
    }
  }

  const saveLabel = base ? `Save as version ${base.versionNumber + 1}` : 'Save rubric';

  return (
    <section className={styles.editor} aria-label={base ? base.name : 'New rubric'} onKeyDown={onKeyDown}>
      <header className={styles.editorHead}>
        <div className={styles.editorTitleRow}>
          <h2 className={styles.editorTitle}>{base ? base.name : 'New rubric'}</h2>
          {base && <Chip tone="neutral">Version {base.versionNumber}</Chip>}
          {base && !isReadOnly && <span className={styles.quiet}>Saving makes version {base.versionNumber + 1}</span>}
        </div>
        {canEdit && !isReadOnly && (
          <div className={styles.headActions}>
            {isDirty && <span className={problems.length > 0 ? styles.problemText : styles.quiet} role="status">{problems.length > 0 ? problems.join(' ') : 'Unsaved changes'}</span>}
            <Button variant="ghost" size="sm" disabled={!isDirty && base !== null} onClick={cancel}>
              Cancel
            </Button>
            <Button variant="solid" size="sm" disabled={!isDirty || problems.length > 0} isPending={save.isPending} onClick={() => void onSave()}>
              {saveLabel}
            </Button>
          </div>
        )}
      </header>

      {prompt}
      {isOlder && (
        <p className={styles.banner} role="status">
          This is version {base.versionNumber}, kept for the rounds that use it. It can't be changed.{' '}
          {current && current.id !== base.id && (
            <Link to="/admin/rubrics/$rubricId" params={{ rubricId: current.id }} className={styles.bannerLink}>
              Open version {current.versionNumber}, the current one
            </Link>
          )}
        </p>
      )}
      {isOvertaken && (
        <p className={styles.bannerDanger} role="alert">
          Someone saved a newer version while you were editing, so these changes can't be saved over it.{' '}
          {current && current.id !== base.id && (
            <Link to="/admin/rubrics/$rubricId" params={{ rubricId: current.id }} className={styles.bannerLink}>
              Open version {current.versionNumber}
            </Link>
          )}
        </p>
      )}

      <div className={styles.form}>
        <FieldRow>
          <Field label="Name" isRequired {...(draft.name !== '' && draft.name.trim().length < 2 ? { error: 'At least 2 letters.' } : {})}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} disabled={isReadOnly} value={draft.name} placeholder="Finance technical interview" onChange={(event) => change({ name: event.target.value })} />
            )}
          </Field>
          <Field label="Rating scale" hint="Kept for the scorecards marked line by line before 01-11-2026.">
            {() => (
              <Segmented
                label="Rating scale"
                disabled={isReadOnly}
                value={String(draft.scaleMax)}
                options={RUBRIC_SCALE_MAXES.map((max) => ({ value: String(max), label: `${RUBRIC_SCALE_MIN}–${max}` }))}
                onChange={(value: string) => change({ scaleMax: asScale(Number(value)) })}
              />
            )}
          </Field>
        </FieldRow>
        <Field label="What it's for" isOptional>
          {({ inputId }) => <Textarea id={inputId} rows={2} disabled={isReadOnly} value={draft.description} placeholder="First-round technical interview for accountants" onChange={(event) => change({ description: event.target.value })} />}
        </Field>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>Lines</h3>
          <span className={styles.quiet}>
            {lines.length} of at most {MAX_RUBRIC_LINES}. Must pass marks a line the candidate has to be strong in, for whoever writes the kit and reads the
            scorecards; it never blocks anyone on its own.
          </span>
        </div>
        <div className={styles.lineHeader} aria-hidden>
          <span />
          <span>Line</span>
          <span>What good looks like</span>
          <span>Must pass</span>
          <span />
        </div>
        <ol className={styles.lines} ref={attach}>
          {lines.map((line, index) => {
            const name = line.label.trim() || `line ${index + 1}`;
            return (
              <li key={line.rowId} className={styles.line} {...itemProps(index)}>
                <span className={styles.lineHandle}>{handle(index, name)}</span>
                <Input
                  aria-label={`Line ${index + 1}`}
                  isInvalid={line.label.trim() === '' && isDirty}
                  disabled={isReadOnly}
                  value={line.label}
                  placeholder="Reconciliations"
                  onChange={(event) => setLine(index, { label: event.target.value })}
                />
                <Input
                  aria-label={`What good looks like for ${name}`}
                  disabled={isReadOnly}
                  value={line.description}
                  placeholder="What good looks like"
                  onChange={(event) => setLine(index, { description: event.target.value })}
                />
                <span className={styles.lineSwitch}>
                  <Switch checked={line.isCritical} disabled={isReadOnly} ariaLabel={`${name}: must pass`} label={line.isCritical ? 'Must pass' : 'No'} onChange={(isCritical) => setLine(index, { isCritical })} />
                </span>
                <span className={styles.lineMenu}>
                  {!isReadOnly && (
                    <MoveMenu
                      label={name}
                      index={index}
                      count={lines.length}
                      onMove={(to) => moveLine(index, to)}
                      {...(lines.length > 1 ? { onRemove: () => change({ lines: lines.filter((_, at) => at !== index) }) } : {})}
                    />
                  )}
                </span>
              </li>
            );
          })}
        </ol>
        {!isReadOnly && lines.length < MAX_RUBRIC_LINES && (
          <div className={styles.addRow}>
            <Button variant="outline" size="sm" icon={Plus} onClick={() => change({ lines: [...lines, newLine()] })}>
              Add a line
            </Button>
          </div>
        )}
      </div>

      {base && <VersionsLine rubricId={base.id} versions={versions.data ?? []} />}

    </section>
  );
}

/** Who saved each version, when, and how many live rounds still use exactly it (the drawing's NEW line). */
function VersionsLine({ rubricId, versions }: { readonly rubricId: string; readonly versions: readonly { readonly id: string; readonly versionNumber: number; readonly isCurrent: boolean; readonly savedAt: string; readonly savedBy: string | null; readonly roundsUsing: number }[] }): ReactNode {
  if (versions.length === 0) return null;
  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Versions</h3>
      <ul className={styles.versions}>
        {versions.map((version) => {
          const text = `${formatDate(version.savedAt)} · ${version.savedBy ?? '—'} · ${version.roundsUsing} round${version.roundsUsing === 1 ? '' : 's'}`;
          return (
            <li key={version.id} className={styles.version}>
              {version.id === rubricId ? (
                <span className={styles.versionThis}>v{version.versionNumber}</span>
              ) : (
                <Link to="/admin/rubrics/$rubricId" params={{ rubricId: version.id }} className={styles.versionLink}>
                  v{version.versionNumber}
                </Link>
              )}
              <span className={styles.quiet}>{text}</span>
              {version.isCurrent && <Chip tone="success">Current</Chip>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function asScale(value: number | undefined): RubricScaleMax {
  return RUBRIC_SCALE_MAXES.find((max) => max === value) ?? DEFAULT_RUBRIC_SCALE_MAX;
}
